// File: frontend/src/services/adminSchedulerService.ts

import { apiClient } from "./apiClient";

// ── DTO shapes (mirror of ScheduledJobDto / SchedulerOverviewDto) ─────────────

/** Outcome of a job's most recent run — mirror of JobRuntimeState.Status. */
export type SchedulerJobStatus =
  | "NEVER_RUN"
  | "RUNNING"
  | "SUCCESS"
  | "FAILED"
  | "SKIPPED";

export interface ScheduledJobDto {
  /** Stable identifier (ScheduledJobType name), sent back on update. */
  code: string;
  /** Spring 6-field cron expression currently configured. */
  cronExpression: string;
  /** The value the job ships with — offered as a reset in the UI. */
  defaultCron: string;
  enabled: boolean;
  /** False when the stored expression does not parse; the job is then not scheduled. */
  cronValid: boolean;
  /** True when a live trigger exists on the server right now. */
  scheduled: boolean;
  /** Next few fire times as bare UTC timestamps — parse with parseServerDate. */
  nextRunTimes: string[];
  lastStartedAt: string | null;
  lastFinishedAt: string | null;
  lastDurationMs: number | null;
  lastStatus: SchedulerJobStatus;
  lastError: string | null;
  lastRunManual: boolean;
  running: boolean;
  totalRuns: number;
  totalFailures: number;
}

export interface SchedulerOverviewDto {
  /** IANA zone the cron expressions are evaluated in. */
  timezone: string;
  /** Server clock at response time (bare UTC). */
  serverTime: string;
  jobs: ScheduledJobDto[];
}

/**
 * One job change. Omitted fields are left untouched by the backend, so the
 * panel can flip a switch without resending the cron expression.
 */
export interface SchedulerJobUpdate {
  code: string;
  cronExpression?: string;
  enabled?: boolean;
}

export interface UpdateSchedulerRequest {
  timezone?: string;
  jobs?: SchedulerJobUpdate[];
}

// ── API helpers ───────────────────────────────────────────────────────────────

/** Current configuration and runtime state of every background job. ADMIN only. */
export const getSchedulerOverview = (): Promise<SchedulerOverviewDto> =>
  apiClient.get<SchedulerOverviewDto>("/api/admin/schedulers");

/**
 * Save timezone and/or job changes. The server validates every cron expression
 * before writing anything, re-arms the triggers, and returns the refreshed
 * overview — so the panel should always render from the response, not from its
 * own optimistic state.
 */
export const updateScheduler = (
  request: UpdateSchedulerRequest
): Promise<SchedulerOverviewDto> =>
  apiClient.put<SchedulerOverviewDto>("/api/admin/schedulers", request);

/**
 * Queue an immediate run of one job without changing its schedule.
 * Works on a disabled job too, which is how a job gets tested before it is
 * switched on.
 */
export const runSchedulerJobNow = (code: string): Promise<SchedulerOverviewDto> =>
  apiClient.post<SchedulerOverviewDto>(`/api/admin/schedulers/${encodeURIComponent(code)}/run`);